import { useContext, useEffect, useRef, useState } from "react";

import { useToast } from "hooks";

import { put as putV1 } from "utils/DeApi";
import { put as putV2 } from "utils/DeV2Api";
import { OrganizationContext } from "contexts/OrganizationProvider";
import { toastConfig as toastConfigDefaults } from "config/toastConfig";

const useUpdateResource = ({
  url = "",
  onError,
  onSuccess,
  apiVersion = "v2",
  hasURLOrgPrefix = false,
  toastConfig = {},
}) => {
  const toast = useToast();
  const subscribedPromises = useRef([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const organization = useContext(OrganizationContext);

  toastConfig = {
    ...toastConfigDefaults,
    success: {
      title: toastConfigDefaults.put.success.title,
      description: toastConfigDefaults.put.success.description,
    },
    error: {
      title: toastConfigDefaults.put.error.title,
      description: toastConfigDefaults.put.error.description,
    },
    ...toastConfig,
  };

  const showToastIfEnabled = (type) => {
    const { show, success, error } = toastConfig || {};
    if (!show) return;
    if (type === "error") return toast.error(error?.title, error?.description);
    return toast.success(success.title, success.description);
  };

  const baseURL = hasURLOrgPrefix
    ? `/organizations/${organization?.id}${url}`
    : url;

  const updateResource = async (requestData = {}) => {
    if (!url) throw new Error("URL is required to update a resource");

    setError(null);
    setIsLoading(true);

    const updatePromise =
      apiVersion === "v2"
        ? putV2(baseURL, requestData)
        : putV1(baseURL, requestData);
    subscribedPromises.current.push(updatePromise);

    try {
      const { data } = await updatePromise.promise;
      setData(data);
      if (typeof onSuccess === "function") onSuccess(data);
      showToastIfEnabled("success", toastConfig.successMessage);
    } catch (error) {
      if (!error.isCanceled) {
        setError(error);
        if (typeof onError === "function") onError(error);
        showToastIfEnabled("error", toastConfig.errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const promises = subscribedPromises.current;
    return () => {
      promises.forEach((promise) => promise.cancel());
    };
  }, []);

  return { error, data, isLoading, updateResource, setError };
};

export default useUpdateResource;
